// Creative detail page — full-screen surface for one post.
//
// Replaces the previous click-tile-to-preview-modal flow. Users land
// here when they click any tile on /gallery (or paste the URL
// directly — refresh-safe, fetches the post on cold load).
//
// Available actions, all in one place:
//   - Publish to selected channels (FB + IG multi-select)
//   - Reimagine via AI prompt → new sibling tile (1 credit)
//   - Schedule → opens ScheduleModal inline (pre-selected to this post)
//   - Download original PNG
//   - Delete (with confirm)
//
// Layout:
//   Desktop: 60/40 split — image left, action panel right
//   Mobile:  stacked single column — image on top, panel scrolls below
//
// Close: back arrow / ESC / backdrop tap → navigate(-1) when there's
// history, else /gallery.
//
// Per-channel publish status is tracked locally so the user sees
// per-channel feedback (FB posted ✓, IG failed ✗) without losing
// progress on one channel because the other errored.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import {
  Post,
  deletePost,
  getPost,
  reimaginePost,
  prepareTextEdit,
  saveCaption,
  listCreativeVersions,
  marqueeEditPreview,
  marqueeUndoPost,
} from '@/lib/gallery';
import {
  InsufficientCreditsError,
  ScheduledPost,
  postToFacebook,
  postToInstagram,
  suggestFacebookCaption,
  suggestInstagramCaption,
} from '@/lib/api';
import { useConfirm } from '@/hooks/useConfirm';
import { useCredits } from '@/hooks/useCredits';
import { useFacebookConnection } from '@/hooks/useFacebookConnection';
import { useBrand } from '@/hooks/useBrand';
import ScheduleModal from '@/pages/schedule/components/ScheduleModal';
import PostToSocialModal from '@/pages/gallery/components/PostToSocialModal';
import StudioFinishEditor from '@/components/feature/StudioFinishEditor';
import CreativeEditor from '@/components/feature/CreativeEditor';
import { useSocialPublishingEnabled, useSchedulingEnabled } from '@/hooks/useFeatureFlags';
import OverlayEditor from '@/components/feature/OverlayEditor';
import {
  flattenComposedFrame,
  type LogoOverlay,
  type TextOverlay,
} from '@/pages/app/lib/composeRender';
// Shape overlays — empty for /creatives but flattenComposedFrame requires the param.
import { getBrand, wizardEditImage } from '@/lib/api';
import {
  saveCreativeVersion,
} from '@/lib/gallery';
import {
  editCohortTiles,
  fetchScheduledByPost,
  summarisePostSchedule,
} from '@/lib/scheduledByPost';

type ChannelId = 'facebook' | 'instagram';

interface ChannelMeta {
  id: ChannelId;
  label: string;
  color: string;
  icon: string;
}

const CHANNELS: ChannelMeta[] = [
  { id: 'facebook', label: 'Facebook', color: '#1877F2', icon: 'ri-facebook-circle-line' },
  { id: 'instagram', label: 'Instagram', color: '#E4405F', icon: 'ri-instagram-line' },
];

// Per-channel publish state. Lives on the page (not in the channel meta)
// so we can reset between publish runs without remounting the row.
type ChannelStatus =
  | { kind: 'idle' }
  | { kind: 'posting' }
  | { kind: 'posted'; at: string }
  | { kind: 'error'; message: string };

const REIMAGINE_COST = 1;

export default function CreativeDetailPage() {
  const { id: postId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const confirm = useConfirm();
  const { refresh: refreshCredits, balance, planTier } = useCredits();

  // location.state.post is the hot-path: gallery navigates here with
  // the post already loaded so we render instantly. On a refresh /
  // direct URL load we fall back to GET /api/gallery/posts/{id}.
  const seededPost = (location.state as { post?: Post } | null)?.post ?? null;
  const [post, setPost] = useState<Post | null>(
    seededPost?.id === postId ? seededPost : null,
  );
  const [loading, setLoading] = useState(!post);
  const [error, setError] = useState<string | null>(null);

  // FB / IG connection state — drives which channels show up in the
  // Publish section. Comes from FacebookConnectionProvider (one fetch
  // app-wide, cached across nav) — June 8 fix for re-pinging Meta on
  // every page click.
  const { status: fbStatus } = useFacebookConnection();
  const { activeBrand } = useBrand();
  const SOCIAL_PUBLISHING_ENABLED = useSocialPublishingEnabled();
  const SCHEDULING_ENABLED = useSchedulingEnabled();

  // Jun 22 — prev/next nav across the gallery's on-screen order. Gallery
  // writes the visible post-id sequence to sessionStorage on every render
  // (skipping stubs since they 404 on detail-page open). We read it on
  // mount + whenever postId changes, find the current index, expose prev
  // + next ids for arrow buttons + Left/Right keyboard nav. Falls back to
  // no-op when storage is empty (refresh on direct URL load) — the user
  // can still hit Back to gallery from the header.
  const navOrder = useMemo<string[]>(() => {
    if (typeof window === 'undefined' || !activeBrand?.id) return [];
    try {
      const raw = sessionStorage.getItem(`creatives_nav:${activeBrand.id}`);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
    } catch { return []; }
  }, [activeBrand?.id, postId]);
  const navIndex = postId ? navOrder.indexOf(postId) : -1;
  const prevId = navIndex > 0 ? navOrder[navIndex - 1] : null;
  const nextId = navIndex >= 0 && navIndex < navOrder.length - 1 ? navOrder[navIndex + 1] : null;
  const goToNeighbor = useCallback((targetId: string | null) => {
    if (!targetId) return;
    navigate(`/creatives/${targetId}`, { replace: true });
  }, [navigate]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Don't hijack keys when user is typing in an input/textarea
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (target?.isContentEditable) return;
      if (e.key === 'ArrowLeft' && prevId) {
        e.preventDefault();
        goToNeighbor(prevId);
      } else if (e.key === 'ArrowRight' && nextId) {
        e.preventDefault();
        goToNeighbor(nextId);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prevId, nextId, goToNeighbor]);

  // Per-channel publish selection (Set so multi-select toggles are
  // O(1)) and per-channel result tracking.
  const [selectedChannels, setSelectedChannels] = useState<Set<ChannelId>>(
    new Set(),
  );
  const [channelStatus, setChannelStatus] = useState<Record<ChannelId, ChannelStatus>>({
    facebook: { kind: 'idle' },
    instagram: { kind: 'idle' },
  });
  // Captions per channel — fetched lazily when the user expands the
  // Publish section. Kept editable so the user can tweak before
  // publishing. Empty string = "use the AI default at post time".
  const [captions, setCaptions] = useState<Record<ChannelId, string>>({
    facebook: '',
    instagram: '',
  });
  const [captionLoading, setCaptionLoading] = useState<Record<ChannelId, boolean>>({
    facebook: false,
    instagram: false,
  });
  const captionFetchedRef = useRef<Set<ChannelId>>(new Set());
  // Tracks the post id we've seeded captions for (from post.captions),
  // so a re-fetch of the post doesn't blow away the user's in-flight
  // edits with the original saved draft.
  const captionsSeededForRef = useRef<string | null>(null);
  // Last value we've persisted to the backend per channel — drives the
  // debounced auto-save effect's "skip no-op writes" check.
  const lastSavedCaptionRef = useRef<Record<ChannelId, string>>({
    facebook: '',
    instagram: '',
  });

  // Reimagine state.
  const [reimaginePromptText, setReimaginePromptText] = useState('');
  const [reimagining, setReimagining] = useState(false);
  const [reimagineError, setReimagineError] = useState<string | null>(null);

  // Version chain (Jun 17). All versions of THIS creative — head + ancestors.
  // Fetched lazily once the post loads; powers the picker dropdown shown
  // when versions.length > 1. The currently displayed image is whatever
  // post we hold in state; switching versions in the picker swaps the
  // displayed post but doesn't navigate.
  const [versions, setVersions] = useState<Post[]>([]);
  const [versionPickerOpen, setVersionPickerOpen] = useState(false);

  // ─── StudioFinishEditor adapter state (Jun 17 extraction wiring) ──
  // The StudioFinishEditor was extracted from /appv3 so we can render
  // the same UX here. Owns all editor-local state on this page; passes
  // it down via props (the editor is presentation-only).
  //
  // `editorFrames` is the displayed image — starts as the post URL,
  // becomes a data: URL after an Apply Edit returns. Save then flattens
  // overlays into this frame and POSTs to /save-version.
  const [editorFrames, setEditorFrames] = useState<string[]>([]);
  const [editorOverlays, setEditorOverlays] = useState<TextOverlay[]>([]);
  const [editorLogoOverlays, setEditorLogoOverlays] = useState<LogoOverlay[]>([]);
  const [editorPrompt, setEditorPrompt] = useState('');
  const [editorPromptEditing, setEditorPromptEditing] = useState(false);
  const [editorEditError, setEditorEditError] = useState<string | null>(null);
  const [editorSaveState, setEditorSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [editorDirty, setEditorDirty] = useState(false);
  const [editorBrandLogoUrl, setEditorBrandLogoUrl] = useState<string | null>(null);
  const [editorBrandColors, setEditorBrandColors] = useState<string[]>([]);
  // Quality retry popover — unused on /creatives (no quality regen) but
  // the prop exists. Stub state so the popover stays closed.
  const [editorRetryOpen, setEditorRetryOpen] = useState(false);
  // Which quality tier is rendering right now (null when idle). Drives
  // the "Rendering high…" pill in the action row + the nav guard modal
  // so the user can't silently lose credits by walking away.
  const [editorRetryRendering, setEditorRetryRendering] = useState<
    'low' | 'medium' | 'high' | null
  >(null);
  // Downloading flag for the editor's download button.
  const [editorDownloading, setEditorDownloading] = useState(false);
  // Marquee edit (Jun 24) — in-flight flags. History count comes straight
  // from post.base_image_path_history.length so it auto-updates when the
  // backend response lands.
  const [marqueeApplying, setMarqueeApplying] = useState(false);
  const [marqueeUndoing, setMarqueeUndoing] = useState(false);
  // Marquee PREVIEW (Jul 17) — a rendered-but-unsaved region edit awaiting the
  // user's Keep/Discard. While set, the editor shows the preview + a confirm
  // bar and locks the other mutating controls. Holds what we need to revert on
  // Discard: the pre-edit base URL + the dirty flag before the edit.
  const [marqueePreview, setMarqueePreview] = useState<
    { originalUrl: string; dirtyBefore: boolean } | null
  >(null);
  // Jul 12 — "Edit Text" activation: removes baked text + seeds editable
  // overlays. On success `post.text_overlay` is set → the OverlayEditor takes
  // over automatically (isTextOverlay flips true).
  const [preparingText, setPreparingText] = useState(false);
  // Aborts the in-flight text-edit fetch (Cancel button). The backend keeps
  // running server-side — abort only stops the client wait.
  const editTextAbortRef = useRef<AbortController | null>(null);
  // Jul 17 — set true when a re-render lands a new base that must be
  // auto-persisted (the user shouldn't have to know to hit Save; a refresh
  // was silently losing the higher-quality render). Consumed by the
  // auto-save effect below once the render settles.
  const pendingAutoSaveRef = useRef(false);
  // Jun 22 — active frame index for multi-frame (carousel) viewing.
  // Singles ignore this; carousels use it to switch between the 4
  // unzipped frames via StudioFinishEditor's thumb row. Resets to 0
  // whenever the displayed post changes (see editorFrames effect).
  const [editorActiveFrameIdx, setEditorActiveFrameIdx] = useState(0);

  // Reset editor state whenever the displayed post changes (initial
  // load, version switch via picker, navigation between creatives).
  //
  // Option B (Jun 17): prefer `base_image_url` so logo + text overlays
  // open as editable layers floating on top of the un-flattened render.
  // Legacy posts (saved before the new model) have null base_image_url
  // and null layer JSONs → editor falls back to image_url with empty
  // arrays, same as the old behavior. New saves move the row into the
  // new model going forward.
  // Reset the active frame ONLY when navigating to a DIFFERENT creative (post
  // id changes) — NOT when the same post's data updates (e.g. after a per-slide
  // Edit Text, which changes base_image_url/overlay_json). Otherwise the editor
  // would jump back to slide 1 after editing slide N.
  useEffect(() => {
    setEditorActiveFrameIdx(0);
    // Drop any stale marquee preview when the displayed creative changes. This
    // is what dismisses the Keep/Discard bar after Keep saves + navigates to
    // the new version (same route component — it never unmounts).
    setMarqueePreview(null);
  }, [post?.id]);

  useEffect(() => {
    if (!post?.image_url) {
      setEditorFrames([]);
      return;
    }
    const isCarouselPost = post.image_path?.endsWith('.zip') ?? false;
    // Singles + videos: editorFrames = [single url]. Carousels: unzip
    // the .zip blob client-side + populate editorFrames with the 4 PNG
    // URLs so StudioFinishEditor's existing frames[].map(thumb) row
    // renders — matches Product Shot's post-render carousel UX (frame
    // switcher below the active hero) instead of a "download to view"
    // placeholder. Reuses the same fflate unzip pattern as the
    // CarouselThumbnail in the gallery grid (Jun 22).
    if (isCarouselPost) {
      let cancelled = false;
      const createdUrls: string[] = [];
      // Show empty until extract completes — editor handles `frames=[]`
      // (renders a blank stage briefly, same as cold-load on singles).
      setEditorFrames([]);
      // Option B: prefer base_image_url when present (un-flattened
      // frames → editor rehydrates overlays as editable layers on top).
      // Falls back to image_url (the flat zip) on legacy / first-save
      // carousels, same as singles fall back when base_image is null.
      const zipUrl = post.base_image_url || post.image_url!;
      (async () => {
        try {
          const res = await fetch(zipUrl);
          if (!res.ok) throw new Error(`zip fetch ${res.status}`);
          const buf = new Uint8Array(await res.arrayBuffer());
          const { unzipSync } = await import('fflate');
          const entries = unzipSync(buf);
          const pngs = Object.entries(entries)
            .filter(([n]) => n.toLowerCase().endsWith('.png'))
            .sort(([a], [b]) => a.localeCompare(b))
            .slice(0, 4);
          if (pngs.length === 0) throw new Error('no frames in zip');
          const urls: string[] = [];
          for (const [, bytes] of pngs) {
            const blob = new Blob([bytes], { type: 'image/png' });
            const url = URL.createObjectURL(blob);
            urls.push(url);
            createdUrls.push(url);
          }
          if (!cancelled) setEditorFrames(urls);
        } catch (err) {
          console.error('[detail] carousel unzip failed', err);
          if (!cancelled) setEditorFrames([]);
        }
      })();
      // Per-entry imageIndex partitions overlays per frame — same
      // schema singles use, so this works without any per-frame map.
      setEditorOverlays(
        Array.isArray(post.overlay_json) ? (post.overlay_json as TextOverlay[]) : [],
      );
      setEditorLogoOverlays(
        Array.isArray(post.logo_overlay_json)
          ? (post.logo_overlay_json as LogoOverlay[])
          : [],
      );
      setEditorPrompt('');
      setEditorEditError(null);
      setEditorSaveState('idle');
      setEditorDirty(false);
      return () => {
        cancelled = true;
        for (const url of createdUrls) URL.revokeObjectURL(url);
      };
    }
    const basePreferred = post.base_image_url || post.image_url;
    setEditorFrames([basePreferred]);
    setEditorOverlays(
      Array.isArray(post.overlay_json) ? (post.overlay_json as TextOverlay[]) : [],
    );
    setEditorLogoOverlays(
      Array.isArray(post.logo_overlay_json)
        ? (post.logo_overlay_json as LogoOverlay[])
        : [],
    );
    setEditorPrompt('');
    setEditorEditError(null);
    setEditorSaveState('idle');
    setEditorDirty(false);
  }, [
    post?.id,
    post?.image_url,
    post?.image_path,
    post?.base_image_url,
    post?.overlay_json,
    post?.logo_overlay_json,
  ]);

  // Brand fetch — drives the logo + color sources in the property panel.
  useEffect(() => {
    let cancelled = false;
    getBrand()
      .then((b) => {
        if (cancelled) return;
        setEditorBrandLogoUrl(b?.logoUrl ?? null);
        setEditorBrandColors(b?.colors ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        setEditorBrandLogoUrl(null);
        setEditorBrandColors([]);
      });
    return () => { cancelled = true; };
  }, []);

  // Schedule modal — mirrors the /gallery hover-icon flow so users can
  // schedule this exact post without bouncing to /schedule. State carries
  // the cohort of tiles being edited (empty array = create mode; non-
  // empty = edit the whole cohort).
  const [scheduling, setScheduling] = useState<{ editingTiles: ScheduledPost[] } | null>(null);
  // Publish-now modal (Jun 17). Distinct from `scheduling` which is the
  // calendar/schedule-for-later UI. The action-row Post button opens this
  // (immediate publish to FB + IG); the inline header "Schedule" button
  // still uses the scheduling modal for future-dated posts.
  const [publishOpen, setPublishOpen] = useState(false);
  // Scheduled tiles for THIS post (refreshed on mount + after onSaved).
  // Drives the "already scheduled?" check on the Schedule button.
  const [postScheduledTiles, setPostScheduledTiles] = useState<ScheduledPost[]>([]);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Image preview state — large hero. Falls back to the public URL on
  // the post record (synthesized by lib/gallery from the GCS bucket).
  const imageUrl = post?.image_url || '';
  const isCarousel = post?.image_path?.endsWith('.zip') ?? false;
  // Animation-route MP4 (Jun 11). Renders as a <video> player; publish +
  // reimagine are image-only flows so both gate off below.
  const isVideo = post?.image_path?.endsWith('.mp4') ?? false;
  // Text-overlay tile (Jun 28) — baked from a JSON text template. Edited via
  // the JSON renderer (OverlayEditor) for pixel-perfect parity, NOT the canvas
  // editor. Null on every other tile → canvas editor path unchanged.
  const isTextOverlay = !!post?.text_overlay;

  // ---------- Lifecycle ----------

  // Cold-load fetch when we don't have the post in state. Also drives
  // re-fetches after Reimagine completes (we navigate to a new id, the
  // useParams change re-mounts this effect).
  useEffect(() => {
    if (!postId) return;
    if (post && post.id === postId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPost(postId)
      .then((p) => {
        if (cancelled) return;
        setPost(p);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load this creative.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [postId, post]);

  // Version chain fetch (Jun 17). Whenever the loaded post changes, pull
  // its full chain. Cheap (one query, oldest-first); only used by the
  // picker. Failing silently is fine — the picker just hides itself.
  useEffect(() => {
    if (!post?.id) return;
    let cancelled = false;
    listCreativeVersions(post.id)
      .then((rows) => {
        if (cancelled) return;
        setVersions(rows);
      })
      .catch(() => {
        if (cancelled) return;
        setVersions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [post?.id]);

  // ─── Editor handlers (Jun 17) ────────────────────────────────────
  // onPromptEdit: AI transformation. Fetches current frame as blob,
  // POSTs to /api/wizard/edit-image, replaces displayed frame with
  // result. Result is NOT yet committed — user must Save to make it a
  // new version.
  const handleEditorPromptEdit = useCallback(async () => {
    const promptText = editorPrompt.trim();
    if (!promptText) return;
    // Jun 22 — multi-frame support: target the ACTIVE frame, not always
    // frame 0. Singles have activeFrameIdx=0 so behavior is unchanged
    // there. Carousels apply the edit to whatever frame the user is
    // viewing; other frames stay untouched.
    const idx = editorActiveFrameIdx;
    if (!editorFrames[idx]) return;
    setEditorPromptEditing(true);
    setEditorEditError(null);
    try {
      const resp = await fetch(editorFrames[idx]);
      const blob = await resp.blob();
      const result = await wizardEditImage({
        image: blob,
        prompt: promptText,
        aspectRatio: post?.aspect_ratio ?? '1:1',
        compositeLogo: false,
      });
      const newFrame = `data:image/png;base64,${result.image_b64}`;
      // Replace ONLY the active frame; keep the others (carousels) OR
      // replace the single (singles — identical to old behavior).
      setEditorFrames((prev) => prev.map((f, i) => (i === idx ? newFrame : f)));
      setEditorDirty(true);
      void refreshCredits();
    } catch (err) {
      console.error('Apply edit failed:', err);
      const msg = err instanceof Error ? err.message : 'Edit failed';
      setEditorEditError(msg);
    } finally {
      setEditorPromptEditing(false);
    }
  }, [editorPrompt, editorFrames, editorActiveFrameIdx, post?.aspect_ratio, refreshCredits]);

  // onManualSave: flatten current frame + overlays → POST /save-version.
  // On success: navigate to new post id (becomes the new head). User
  // returns to /gallery via header back button.
  //
  // CORS gotcha: flattenComposedFrame loads the base image WITHOUT
  // crossOrigin='anonymous', so a cross-origin GCS URL taints the
  // canvas and toBlob() throws SecurityError. We pre-fetch the image
  // as a Blob and pass a same-origin blob: URL so the canvas stays
  // clean. (Fetch goes through CORS preflight; works because the
  // gallery bucket has Access-Control-Allow-Origin: * per the GCS
  // setup script.) Skip flatten entirely when there are no overlays
  // — just send the raw bytes.
  const handleEditorSave = useCallback(async () => {
    if (!post?.id) return;
    if (!editorFrames[0]) return;
    // Dirty-guard (Jul 17): every save forks a new version, so a no-op Save
    // (opened + clicked Save with no edits) would spawn a redundant version.
    // Skip when nothing changed. The re-render auto-save always sets
    // editorDirty=true before this runs, and overlay/logo/prompt edits all set
    // it too, so this only blocks a genuine no-change save.
    if (!editorDirty) {
      setToast({ message: 'No changes to save.', type: 'success' });
      return;
    }
    setEditorSaveState('saving');
    const objectUrls: string[] = [];
    try {
      const isCarouselSave = (post?.image_path?.endsWith('.zip') ?? false)
        && editorFrames.length > 1;

      // Helper — fetch any editor frame URL (data: OR blob:) as a Blob.
      // Same code path either way; the `fetch()` API handles both.
      const fetchAsBlob = async (src: string): Promise<Blob> => {
        const resp = await fetch(src);
        return resp.blob();
      };

      // Per-frame flatten. Returns { flat, base }: `flat` has overlays
      // baked in (what the gallery shows + downloads); `base` is the raw
      // render with no overlays (what the editor re-hydrates from on
      // reopen). For frames with no overlays attached, flat === base
      // (saves one canvas pass).
      const flattenFrame = async (frameIdx: number) => {
        const sourceBlob = await fetchAsBlob(editorFrames[frameIdx]);
        const overlaysForFrame = editorOverlays.filter(
          (o) => o.imageIndex === frameIdx,
        );
        const logosForFrame = editorLogoOverlays.filter(
          (l) => l.imageIndex === frameIdx,
        );
        const hasOverlays =
          overlaysForFrame.length > 0 || logosForFrame.length > 0;
        if (!hasOverlays) {
          return { flat: sourceBlob, base: sourceBlob };
        }
        // Flatten over a same-origin blob URL so the canvas isn't
        // tainted by the cross-origin GCS source.
        const objectUrl = URL.createObjectURL(sourceBlob);
        objectUrls.push(objectUrl);
        const flat = await flattenComposedFrame({
          imageSrc: objectUrl,
          frameIndex: frameIdx,
          overlays: overlaysForFrame,
          shapeOverlays: [],
          logoOverlays: logosForFrame,
        });
        if (!flat) throw new Error(`Could not flatten frame ${frameIdx + 1}.`);
        return { flat, base: sourceBlob };
      };

      let outputBlob: Blob;
      let baseBlob: Blob;

      if (isCarouselSave) {
        // Carousel — flatten each frame independently, then re-zip BOTH
        // outputs (flat = display, base = re-editable on reopen) into
        // .zip blobs with `frame1..4.png` naming so existing readers
        // (CarouselThumbnail + the gallery tile + the detail-page unzip
        // effect) keep working unchanged.
        const { zip } = await import('fflate');
        const perFrame = await Promise.all(
          editorFrames.map((_, i) => flattenFrame(i)),
        );
        const flatEntries: Record<string, Uint8Array> = {};
        const baseEntries: Record<string, Uint8Array> = {};
        for (let i = 0; i < perFrame.length; i++) {
          flatEntries[`frame${i + 1}.png`] = new Uint8Array(
            await perFrame[i].flat.arrayBuffer(),
          );
          baseEntries[`frame${i + 1}.png`] = new Uint8Array(
            await perFrame[i].base.arrayBuffer(),
          );
        }
        const flatZipped: Uint8Array = await new Promise((resolve, reject) => {
          zip(flatEntries, (err, data) => (err ? reject(err) : resolve(data)));
        });
        const baseZipped: Uint8Array = await new Promise((resolve, reject) => {
          zip(baseEntries, (err, data) => (err ? reject(err) : resolve(data)));
        });
        outputBlob = new Blob([flatZipped.buffer as ArrayBuffer], { type: 'application/zip' });
        baseBlob = new Blob([baseZipped.buffer as ArrayBuffer], { type: 'application/zip' });
      } else {
        // Single — original code path. Frame 0 only.
        const single = await flattenFrame(0);
        outputBlob = single.flat;
        baseBlob = single.base;
      }

      // Option B (Jun 17): persist the RAW base separately so layers
      // stay editable on reopen. `baseBlob` IS the raw base (carousel
      // zip OR single png); `outputBlob` is what gallery shows. The
      // overlay JSON arrays already carry per-entry imageIndex, so
      // multi-frame carousels and single-frame singles use the SAME
      // schema — backend doesn't need to know which is which.
      const newPost = await saveCreativeVersion({
        sourcePostId: post.id,
        blob: outputBlob,
        baseBlob,
        overlayJson: editorOverlays,
        logoOverlayJson: editorLogoOverlays,
      });
      setEditorSaveState('saved');
      setEditorDirty(false);
      // REPLACE — don't push. Otherwise the back button hits the now-
      // stale /creatives/<old-id> (which is no longer is_head + would
      // need a refetch) before it gets to /gallery. One back = /gallery.
      navigate(`/creatives/${newPost.id}`, {
        state: { post: newPost },
        replace: true,
      });
    } catch (err) {
      console.error('Save version failed:', err);
      setEditorSaveState('error');
    } finally {
      for (const url of objectUrls) URL.revokeObjectURL(url);
    }
  }, [post, editorFrames, editorOverlays, editorLogoOverlays, editorDirty, navigate]);

  // Editor's download button — same blob-fetch as the existing download
  // handler, but using the displayed frame (which may be a data: URL
  // after an Apply Edit, not the original GCS URL).
  const handleEditorDownload = useCallback(async () => {
    if (!editorFrames[0]) return;
    setEditorDownloading(true);
    try {
      const resp = await fetch(editorFrames[0]);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(post?.title || 'creative').replace(/[^a-z0-9-_]/gi, '_')}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setEditorDownloading(false);
    }
  }, [editorFrames, post?.title]);

  const handleEditorToggleRetry = useCallback(() => setEditorRetryOpen((o) => !o), []);
  const handleEditorCloseRetry = useCallback(() => setEditorRetryOpen(false), []);
  const handleEditorRetrySave = useCallback(() => {
    void handleEditorSave();
  }, [handleEditorSave]);

  // Re-render the BASE (no layers baked in) at a higher quality. Calls
  // /api/wizard/edit-image with a generic re-render prompt + the picked
  // quality. Credit cost scales 1/2/4 via the backend's calculate_cost.
  // Overlays stay in editor state untouched — they re-render on top of
  // the new base, same model as the AI-prompt-edit flow.
  const handleEditorRegenerateWithQuality = useCallback(
    async (q: 'low' | 'medium' | 'high') => {
      if (!editorFrames[0]) return;
      if (editorRetryRendering) return; // already in flight
      setEditorRetryOpen(false);
      setEditorRetryRendering(q);
      setEditorEditError(null);
      try {
        const resp = await fetch(editorFrames[0]);
        const blob = await resp.blob();
        const result = await wizardEditImage({
          image: blob,
          prompt:
            'Re-render this advertising image with enhanced clarity, sharper detail, and refined typography. Preserve the composition, subject, layout, and palette exactly — only improve fidelity.',
          aspectRatio: post?.aspect_ratio ?? '1:1',
          compositeLogo: false,
          quality: q,
        });
        const newFrame = `data:image/png;base64,${result.image_b64}`;
        setEditorFrames([newFrame]);
        setEditorDirty(true);
        // Auto-persist this render — the auto-save effect fires once the
        // render settles (editorRetryRendering → null) so it saves the NEW
        // frame, not the stale one (avoids the setState-is-async closure trap).
        pendingAutoSaveRef.current = true;
        void refreshCredits();
      } catch (err) {
        console.error('Re-render failed:', err);
        const msg = err instanceof Error ? err.message : 'Re-render failed';
        setEditorEditError(msg);
      } finally {
        setEditorRetryRendering(null);
      }
    },
    [editorFrames, editorRetryRendering, post?.aspect_ratio, refreshCredits],
  );

  // Auto-save a completed re-render (Jul 17). A quality re-render only lands the
  // new base in editor state — it isn't persisted until Save, and the user
  // doesn't know they must Save, so a refresh silently reverted to the old
  // render. Once the render settles, persist it via the SAME path as manual
  // Save (new version → becomes head), so the higher-quality render survives a
  // refresh and the prior render stays available in the version picker. Runs in
  // an effect (not inline in the handler) so it reads the UPDATED editorFrames
  // rather than the stale pre-setState value.
  useEffect(() => {
    if (!pendingAutoSaveRef.current) return;
    if (editorRetryRendering !== null) return; // wait until the render fully settles
    if (editorSaveState === 'saving') return;  // don't stack onto an in-flight save
    pendingAutoSaveRef.current = false;
    void handleEditorSave();
  }, [editorFrames, editorRetryRendering, editorSaveState, handleEditorSave]);

  // Marquee edit (Jun 24) — apply a partial region edit. Calls
  // /api/creatives/:id/marquee-edit which builds a feathered mask, calls
  // gpt-image-2 mask edit with the instruction, swaps the base image. We
  // refresh the post + reset editorFrames so the new base is what the
  // editor draws on top of. Text/logo overlays stay in editor state
  // untouched — they ride on top of the new base.
  const handleMarqueeApply = useCallback(
    async (
      rect: { x: number; y: number; w: number; h: number },
      instruction: string,
    ) => {
      if (!post) return;
      if (marqueeApplying || marqueePreview) return;
      // Capture what we'll revert to if the user Discards. editorFrames[0] is
      // the currently-displayed base (original render OR a prior kept edit).
      const originalUrl = editorFrames[0];
      const dirtyBefore = editorDirty;
      setMarqueeApplying(true);
      setEditorEditError(null);
      try {
        // Preview mode: renders + charges the credit, but persists NOTHING.
        const { previewUrl } = await marqueeEditPreview(post.id, rect, instruction);
        // Show the edit as the displayed base — overlays ride on top. Nothing
        // is saved until Keep. Mark dirty so the confirm + save path engage.
        setEditorFrames([previewUrl]);
        setEditorDirty(true);
        setMarqueePreview({ originalUrl, dirtyBefore });
        void refreshCredits();
      } catch (err) {
        console.error('Marquee edit failed:', err);
        const msg = err instanceof Error ? err.message : 'Marquee edit failed';
        setEditorEditError(msg);
      } finally {
        setMarqueeApplying(false);
      }
    },
    [post, marqueeApplying, marqueePreview, editorFrames, editorDirty, refreshCredits],
  );

  // Keep — the user is happy with the marquee preview. Fork a new version via
  // the SAME save path as manual Save / re-render auto-save (flatten preview
  // base + overlays → saveCreativeVersion → becomes head → navigate). The
  // credit was already charged when the render ran; save is free.
  const handleMarqueeKeep = useCallback(async () => {
    if (!marqueePreview) return;
    // Keep the confirm bar mounted through the save so it can show "Saving…".
    // On success handleEditorSave navigates away (unmount clears everything).
    // On failure it sets saveState='error' and the bar stays so the user can
    // retry Keep or Discard.
    await handleEditorSave();
  }, [marqueePreview, handleEditorSave]);

  // Discard — revert to the pre-edit image. The credit stays spent (the render
  // ran; the model doesn't refund). Restore the prior dirty flag so any edits
  // that predated the marquee (overlays, etc.) aren't falsely marked clean.
  const handleMarqueeDiscard = useCallback(() => {
    if (!marqueePreview) return;
    setEditorFrames([marqueePreview.originalUrl]);
    setEditorDirty(marqueePreview.dirtyBefore);
    setMarqueePreview(null);
    setEditorEditError(null);
  }, [marqueePreview]);

  // "Edit Text" — remove the baked-in text + seed editable overlays (2 credits).
  // On success the post gains base_image_path (text-free plate) + overlay_json
  // (the text as TextOverlay[] layers). The mount effect re-fires and the SAME
  // StudioFinishEditor rehydrates with the plate + editable layers — the rich
  // editor (Edit-with-AI + Re-render + Marquee + Post) stays; no bare-editor swap.
  const handleEditText = useCallback(async () => {
    if (!post || preparingText) return;
    // Heads-up before charging: text removal is not always clean.
    const ok = await confirm({
      title: 'Make the text editable?',
      message:
        'Editing the text might not always give the expected results — it works '
        + 'better when the text is clearly separated from the image.',
      confirmLabel: 'Continue',
      cancelLabel: 'Cancel',
      icon: 'ri-text',
    });
    if (!ok) return;
    const controller = new AbortController();
    editTextAbortRef.current = controller;
    setPreparingText(true);
    try {
      // Carousels are per-slide: clean only the slide currently in view (2 cr).
      const frameIndex = isCarousel ? editorActiveFrameIdx : undefined;
      const updated = await prepareTextEdit(post.id, {
        frameIndex,
        signal: controller.signal,
      });
      setPost(updated);
      void refreshCredits();
      setToast({ message: 'Text is now editable — tweak it below.', type: 'success' });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        // User canceled — the removal may still finish + charge on the server.
        setToast({
          message: 'Canceled — the removal may still finish on the server.',
          type: 'error',
        });
        void refreshCredits();
      } else {
        // Timeout (504) auto-refunds server-side; reflect the balance either way.
        const msg = err instanceof Error ? err.message : 'Could not prepare text editing';
        setToast({ message: msg, type: 'error' });
        void refreshCredits();
      }
    } finally {
      setPreparingText(false);
      editTextAbortRef.current = null;
    }
  }, [post, preparingText, refreshCredits, confirm, isCarousel, editorActiveFrameIdx]);

  // Cancel the in-flight text edit. Aborts the client wait; warn that the
  // server-side removal may still complete + use credits.
  const handleCancelEditText = useCallback(async () => {
    const ok = await confirm({
      title: 'Cancel text edit?',
      message:
        'Canceling stops the wait, but the removal is already running on the server '
        + 'and may still finish and use your credits. Cancel anyway?',
      confirmLabel: 'Cancel anyway',
      cancelLabel: 'Keep waiting',
      destructive: true,
    });
    if (!ok) return;
    editTextAbortRef.current?.abort();
  }, [confirm]);

  // Marquee undo — pops latest entry off base_image_path_history server-
  // side, returns updated Post with base_image_path restored. Free.
  const handleMarqueeUndo = useCallback(async () => {
    if (!post) return;
    if (marqueeUndoing) return;
    setMarqueeUndoing(true);
    setEditorEditError(null);
    try {
      const updated = await marqueeUndoPost(post.id);
      setPost(updated);
      const newBaseUrl = updated.base_image_url ?? updated.image_url;
      if (newBaseUrl) {
        setEditorFrames([newBaseUrl]);
      }
      setEditorDirty(true);
    } catch (err) {
      console.error('Marquee undo failed:', err);
      const msg = err instanceof Error ? err.message : 'Marquee undo failed';
      setEditorEditError(msg);
    } finally {
      setMarqueeUndoing(false);
    }
  }, [post, marqueeUndoing]);

  // fbStatus comes from FacebookConnectionProvider — no per-mount fetch
  // needed here.

  // ESC closes — bounces back to /gallery (or the previous page in
  // history if there is one). Skip when typing in the reimagine
  // textarea so ESC doesn't yank the user out mid-prompt.
  // Jun 17 — if a render is in flight, ask before leaving (1 credit
  // already spent, navigation cancels the in-progress output).
  const handleClose = useCallback(async () => {
    // Guard nav while ANY render is in flight: reimagine (legacy),
    // editor prompt-edit, or Re-render Low/Med/High. Each one has
    // already reserved (and possibly spent) credits, and the output
    // lands on the page — leaving cancels it server-side too late
    // to refund.
    // Edit Text: the backend finishes server-side even if you leave (credits
    // are already charged), so it's NOT cancelled — you just lose the on-screen
    // result until you reopen. Different message from the render case.
    if (preparingText) {
      const ok = await confirm({
        title: 'Making the text editable…',
        message:
          "This finishes on the server even if you leave — the credits are already "
          + "charged. You just won't see the result here until you reopen this creative. Leave anyway?",
        confirmLabel: 'Leave anyway',
        cancelLabel: 'Stay',
      });
      if (!ok) return;
    }
    // A marquee edit is rendered but not yet saved (awaiting Keep/Discard) —
    // leaving drops it. The render already ran, so the credit is spent.
    if (marqueePreview) {
      const ok = await confirm({
        title: 'Discard the unsaved edit?',
        message:
          "You have an edit that hasn't been saved yet. Leaving will discard it — "
          + 'the credit for the edit is already spent. Leave anyway?',
        confirmLabel: 'Leave anyway',
        cancelLabel: 'Stay',
        destructive: true,
      });
      if (!ok) return;
    }
    const inFlight =
      reimagining || editorPromptEditing || editorRetryRendering !== null
      || marqueeApplying || marqueeUndoing;
    if (inFlight) {
      const label = editorRetryRendering
        ? `re-render (${editorRetryRendering})`
        : (marqueeApplying || marqueeUndoing)
          ? 'region edit'
          : 'render';
      const ok = await confirm({
        title: `${label[0].toUpperCase()}${label.slice(1)} in progress`,
        message:
          `Leaving now will cancel the ${label}. You'll lose the credits and the output. Are you sure?`,
        confirmLabel: 'Leave anyway',
        cancelLabel: 'Stay',
        destructive: true,
      });
      if (!ok) return;
    }
    if (window.history.length > 1) navigate(-1);
    else navigate('/gallery');
  }, [
    navigate, reimagining, editorPromptEditing, editorRetryRendering,
    preparingText, marqueeApplying, marqueeUndoing, marqueePreview, confirm,
  ]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose]);

  // Browser-level guard for tab close / refresh / external nav while a
  // render is in flight. We can't show our nice confirm modal here —
  // browsers force their own dialog from a beforeunload returnValue.
  // Same trigger as handleClose so the user gets warned regardless of
  // exit path.
  useEffect(() => {
    const inFlight =
      reimagining || editorPromptEditing || editorRetryRendering !== null
      || preparingText || marqueeApplying || marqueeUndoing
      || marqueePreview !== null;
    if (!inFlight) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [
    reimagining, editorPromptEditing, editorRetryRendering, preparingText,
    marqueeApplying, marqueeUndoing, marqueePreview,
  ]);

  // (Initial fetch of this post's scheduled tiles is registered AFTER
  // refreshScheduledTiles is declared further down — putting it here
  // would TDZ-crash on render because the deps array reads the const
  // before its `useCallback` declaration line. See below.)

  // Auto-dismiss toasts. Success = 3s; error = 8s. Errors are also dumped
  // to the console so they don't vanish without a trace.
  useEffect(() => {
    if (!toast) return;
    if (toast.type === 'error') console.error('[detail toast]', toast.message);
    const dwellMs = toast.type === 'error' ? 8000 : 3000;
    const t = setTimeout(() => setToast(null), dwellMs);
    return () => clearTimeout(t);
  }, [toast]);

  // Seed captions from the post's saved drafts (post.captions) the first
  // time we see this post id. Channels that already have a saved draft
  // get marked in captionFetchedRef so handleToggleChannel won't fire
  // a wasteful AI-suggest call when the user enables them.
  useEffect(() => {
    if (!post) return;
    if (captionsSeededForRef.current === post.id) return;
    captionsSeededForRef.current = post.id;
    const fb = post.captions?.facebook ?? '';
    const ig = post.captions?.instagram ?? '';
    setCaptions({ facebook: fb, instagram: ig });
    if (fb) captionFetchedRef.current.add('facebook');
    if (ig) captionFetchedRef.current.add('instagram');
    lastSavedCaptionRef.current = { facebook: fb, instagram: ig };
  }, [post]);

  // Debounced auto-save of per-channel caption edits. Fires 1.5s after
  // the last keystroke; persists only channels whose value differs from
  // the last-saved snapshot. Skips while a fresh AI suggestion is in
  // flight (captionLoading[channel] === true) so we don't write the
  // placeholder string. Save failures are silent — the next edit will
  // retry, and the publish path also saves synchronously below.
  useEffect(() => {
    if (!post) return;
    const timer = setTimeout(() => {
      for (const channel of ['facebook', 'instagram'] as const) {
        if (captionLoading[channel]) continue;
        const snap = captions[channel];
        if (snap === lastSavedCaptionRef.current[channel]) continue;
        saveCaption(post.id, channel, snap)
          .then(() => { lastSavedCaptionRef.current[channel] = snap; })
          .catch((err) => console.warn(`[detail] save caption ${channel} failed:`, err));
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [captions, captionLoading, post]);

  // Flush pending caption edits on unmount (navigating away, closing
  // the tab, etc.). Without this, a user who edits then leaves within
  // the 1.5s debounce window loses their draft. Refs let the cleanup
  // read the latest values without re-binding on every render.
  const captionsRef = useRef(captions);
  captionsRef.current = captions;
  const postIdRef = useRef<string | null>(post?.id ?? null);
  postIdRef.current = post?.id ?? null;
  useEffect(() => {
    return () => {
      const pid = postIdRef.current;
      if (!pid) return;
      const snap = captionsRef.current;
      for (const channel of ['facebook', 'instagram'] as const) {
        if (snap[channel] !== lastSavedCaptionRef.current[channel]) {
          saveCaption(pid, channel, snap[channel]).catch(() => undefined);
        }
      }
    };
    // Mount/unmount only — keep deps empty so the cleanup reads from
    // the refs (always-fresh) rather than re-binding per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Derived ----------

  const channelsConnected = useMemo(() => {
    const out: Record<ChannelId, boolean> = {
      facebook: !!fbStatus?.connected,
      // IG rides on the FB connection — only connected when the FB
      // Page has a linked IG Business Account discovered at OAuth time.
      instagram: !!(fbStatus?.connected && fbStatus?.instagramUserId),
    };
    return out;
  }, [fbStatus]);

  const channelsConnectedCount = useMemo(
    () => Object.values(channelsConnected).filter(Boolean).length,
    [channelsConnected],
  );

  // ---------- Handlers ----------

  // Toggle a channel in the publish multi-select. Also lazy-fetches
  // that channel's suggested caption the first time it's enabled.
  const handleToggleChannel = useCallback(
    (channel: ChannelId) => {
      setSelectedChannels((prev) => {
        const next = new Set(prev);
        if (next.has(channel)) next.delete(channel);
        else next.add(channel);
        return next;
      });
      // Lazy caption fetch (only once per channel per page-life).
      if (
        post &&
        !captionFetchedRef.current.has(channel) &&
        channelsConnected[channel]
      ) {
        captionFetchedRef.current.add(channel);
        setCaptionLoading((p) => ({ ...p, [channel]: true }));
        const suggester =
          channel === 'facebook' ? suggestFacebookCaption : suggestInstagramCaption;
        suggester(post.id)
          .then(({ caption }) => {
            setCaptions((p) => ({ ...p, [channel]: caption }));
          })
          .catch((err) => {
            console.warn(`[detail] ${channel} caption fetch failed:`, err);
          })
          .finally(() => {
            setCaptionLoading((p) => ({ ...p, [channel]: false }));
          });
      }
    },
    [post, channelsConnected],
  );

  // Regenerate a caption for the given channel — overwrites whatever's
  // in the textarea AND triggers the auto-save effect via the captions
  // state change. Mirrors the "Regenerate with AI" pattern from the
  // wizard's StepPreview + the PostToSocialModal.
  const handleRegenerateCaption = useCallback(
    async (channel: ChannelId) => {
      if (!post) return;
      setCaptionLoading((p) => ({ ...p, [channel]: true }));
      const suggester =
        channel === 'facebook' ? suggestFacebookCaption : suggestInstagramCaption;
      try {
        const { caption } = await suggester(post.id);
        setCaptions((p) => ({ ...p, [channel]: caption }));
      } catch (err) {
        console.warn(`[detail] regenerate ${channel} failed:`, err);
      } finally {
        setCaptionLoading((p) => ({ ...p, [channel]: false }));
      }
    },
    [post],
  );

  // Publish to all selected channels in parallel. Per-channel status
  // updates as each settles — a Promise.allSettled-style fan-out so
  // one failure doesn't block the others.
  const handlePublish = useCallback(async () => {
    if (!post) return;
    const channels = Array.from(selectedChannels);
    if (channels.length === 0) return;

    // Synchronous best-effort save of the latest text per channel before
    // we publish. Auto-save may not have fired yet if the user typed
    // and immediately clicked Post — without this, a refresh after
    // publish would restore the stale saved value instead of the one
    // we just sent to FB/IG.
    for (const channel of channels) {
      const snap = captions[channel] || '';
      if (snap !== lastSavedCaptionRef.current[channel]) {
        saveCaption(post.id, channel, snap)
          .then(() => { lastSavedCaptionRef.current[channel] = snap; })
          .catch(() => undefined);
      }
    }

    // Reset status for selected channels.
    setChannelStatus((prev) => {
      const next = { ...prev };
      for (const c of channels) next[c] = { kind: 'posting' };
      return next;
    });

    await Promise.allSettled(
      channels.map(async (channel) => {
        try {
          const publisher = channel === 'facebook' ? postToFacebook : postToInstagram;
          const caption = captions[channel] || '';
          await publisher(post.id, caption);
          setChannelStatus((prev) => ({
            ...prev,
            [channel]: { kind: 'posted', at: new Date().toISOString() },
          }));
        } catch (err) {
          const message =
            err instanceof Error ? err.message : `Publish to ${channel} failed`;
          console.error(`[detail] publish ${channel} failed:`, err);
          setChannelStatus((prev) => ({
            ...prev,
            [channel]: { kind: 'error', message },
          }));
        }
      }),
    );

    // Re-fetch the post so the published_to array (and thus the
    // PublicationPill on /gallery later) is fresh.
    try {
      const refreshed = await getPost(post.id);
      setPost(refreshed);
    } catch (err) {
      console.warn('[detail] post refresh after publish failed:', err);
    }
  }, [post, selectedChannels, captions]);

  // Reimagine — 1 credit, blocking ~25s, returns a new post.
  // On success we navigate to the new post's detail page so the user
  // sees the result immediately. The original is preserved.
  const handleReimagine = useCallback(async () => {
    if (!post) return;
    const prompt = reimaginePromptText.trim();
    if (!prompt) return;
    if (typeof balance === 'number' && balance < REIMAGINE_COST) {
      setReimagineError(
        `You need ${REIMAGINE_COST} credit to reimagine. Upgrade to continue.`,
      );
      return;
    }
    setReimagining(true);
    setReimagineError(null);
    try {
      const newPost = await reimaginePost(post.id, prompt);
      // Credit balance changed — sync.
      void refreshCredits();
      // Navigate to the new tile's detail (preserves the original).
      // REPLACE — same reason as the editor-save path: one back =
      // /gallery, never the stale prior /creatives/<old-id>.
      navigate(`/creatives/${newPost.id}`, {
        state: { post: newPost },
        replace: true,
      });
      // Reset prompt so if the user navigates back they're starting fresh.
      setReimaginePromptText('');
    } catch (err) {
      if (err instanceof InsufficientCreditsError) {
        setReimagineError(
          err.message ||
            `Need ${REIMAGINE_COST} credit to reimagine. Upgrade to continue.`,
        );
      } else {
        setReimagineError(
          err instanceof Error ? err.message : 'Reimagine failed. Try again.',
        );
      }
    } finally {
      setReimagining(false);
    }
  }, [post, reimaginePromptText, balance, navigate, refreshCredits]);

  // Refresh this post's scheduled tile list. Called on mount and after
  // any schedule mutation (create/edit/cancel) so the Schedule button
  // reflects the current state without a full page reload.
  const refreshScheduledTiles = useCallback(async () => {
    if (!post) return;
    try {
      const map = await fetchScheduledByPost();
      setPostScheduledTiles(map[post.id] ?? []);
    } catch {
      /* swallow — pill will just show "Schedule" instead of "Edit schedule" */
    }
  }, [post]);

  // Initial fetch on mount + whenever the active post changes. Declared
  // AFTER refreshScheduledTiles to avoid a TDZ ReferenceError on render
  // (the deps array evaluates synchronously; a `const` reference before
  // its declaration line throws even though TypeScript can't see it).
  useEffect(() => {
    void refreshScheduledTiles();
  }, [refreshScheduledTiles]);

  // Schedule — open ScheduleModal inline with this post pre-selected.
  // If a schedule already exists, open in EDIT mode for the cohort
  // (every platform sharing the next-upcoming scheduled_at) so the user
  // can toggle platforms, move time, or cancel. Otherwise create fresh.
  const handleSchedule = useCallback(() => {
    if (!post) return;
    const cohort = editCohortTiles(postScheduledTiles);
    setScheduling({ editingTiles: cohort });
  }, [post, postScheduledTiles]);

  // Publish-now (the action-row "Post" button). Opens the unified
  // PostToSocialModal with BOTH channels pre-enabled — user can toggle
  // one off to skip it. Each channel has its own draft caption that
  // persists between modal opens via posts.captions.
  const handlePost = useCallback(() => {
    if (!post) return;
    setPublishOpen(true);
  }, [post]);

  // Download — same pipeline as the gallery hover icon.
  const handleDownload = useCallback(async () => {
    if (!post || !imageUrl) return;
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const ext = isCarousel ? 'zip' : isVideo ? 'mp4' : 'png';
      const a = document.createElement('a');
      a.href = url;
      a.download = `${post.title || 'creative'}-${post.id.slice(0, 8)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  }, [post, imageUrl, isCarousel]);

  const handleDelete = useCallback(async () => {
    if (!post) return;
    const ok = await confirm({
      title: 'Delete this creative?',
      message:
        'This will permanently remove it from My Creatives. Already-published posts on Facebook / Instagram remain live.',
      destructive: true,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await deletePost(post.id);
      navigate('/gallery');
    } catch (err) {
      console.error('Delete failed:', err);
    }
  }, [post, confirm, navigate]);

  // ---------- Render ----------

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <i className="ri-error-warning-line text-4xl text-dark-muted mb-3 block" />
          <h2 className="text-lg font-bold text-dark mb-2">Could not load this creative</h2>
          <p className="text-sm text-dark-muted mb-4">{error || 'Post not found.'}</p>
          <Link
            to="/gallery"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-white text-sm font-bold hover:bg-primary-hover"
          >
            <i className="ri-arrow-left-line" />
            Back to My Creatives
          </Link>
        </div>
      </div>
    );
  }

  // Jul 19 — My Creatives now opens in the SHARED CreativeEditor using the
  // UNIFIED v5 docked layout (same editor the Product Shot wizard uses), for
  // BOTH single-image AND carousel (.zip) posts — CreativeEditor unzips a
  // carousel on load + edits each frame (thumbnail strip, per-frame overlays,
  // per-frame AI-edit + region-edit, zip save/download). The v4 (classic
  // floating-panel) layout is NOT gone — it's still the default variant and is
  // used by /editor-v4/:id, so we can switch back by dropping variant="v5".
  // Only videos (.mp4) + Model-B text-overlay tiles keep the legacy surfaces.
  if (!isVideo && !isTextOverlay) {
    return (
      <CreativeEditor
        variant="v5"
        initialPostId={post.id}
        initialPost={post}
        // Back returns to WHEREVER the user came from (Ad Strategist, My
        // Creatives, a wizard, etc.) — every entry point reaches the editor
        // via a normal push navigation, so history remembers the referrer.
        // Falls back to My Creatives on a direct link / refresh (no history).
        // Label is neutral "Back" since the origin varies. (onActivePostChange
        // uses replace, so switching frames doesn't pollute the back target.)
        backLabel="Back"
        onBack={() => {
          if (window.history.length > 1) navigate(-1);
          else navigate('/gallery');
        }}
        onActivePostChange={(pid) => navigate(`/creatives/${pid}`, { replace: true })}
        onDeleted={() => navigate('/gallery')}
        showDelete
      />
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      {/* Jul 14 — full-page blocking overlay while Edit Text runs. Covers the
          whole viewport (z-50: above page nav/header, below the z-[60] confirm
          modal so an Escape → "leave anyway?" prompt still shows). Captures all
          pointer events so no other button/action can fire mid-process. The
          backend finishes server-side regardless, but this prevents accidental
          double-charges + conflicting edits. */}
      {preparingText && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm cursor-wait"
          role="alertdialog"
          aria-busy="true"
          aria-label="Making the text editable"
        >
          <div className="max-w-sm mx-4 rounded-2xl bg-white shadow-2xl border border-surface-dark/20 px-6 py-7 text-center">
            <i className="ri-loader-4-line animate-spin text-4xl text-primary block mb-3" />
            <p className="text-base font-bold text-dark">Making the text editable…</p>
            <p className="mt-2 text-sm text-dark-muted leading-relaxed">
              Usually under a minute. If it can't finish, it times out after 3
              minutes and your credits are refunded automatically.
            </p>
            <button
              type="button"
              onClick={handleCancelEditText}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-surface-dark/40 bg-white text-sm font-bold text-dark hover:border-red-400 hover:text-red-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line" />
              Cancel
            </button>
            <p className="mt-2 text-xs text-dark-muted/70">
              Canceling stops the wait, but the removal may still finish and use
              your credits.
            </p>
          </div>
        </div>
      )}
      {/* Jun 22 — floating prev/next arrows. Walk the gallery's visible
          on-screen order (sessionStorage `creatives_nav:{brand_id}`,
          written by the gallery on every render). Disabled at the
          edges. Keyboard: Left/Right arrow keys do the same nav. */}
      {prevId && (
        <button
          onClick={() => goToNeighbor(prevId)}
          className="fixed left-3 top-1/2 -translate-y-1/2 z-40 w-12 h-12 rounded-full bg-white/90 backdrop-blur border border-surface-dark/40 shadow-lg hover:shadow-xl hover:bg-white text-dark flex items-center justify-center transition-all"
          aria-label="Previous creative"
          title="Previous creative (←)"
        >
          <i className="ri-arrow-left-s-line text-2xl" />
        </button>
      )}
      {nextId && (
        <button
          onClick={() => goToNeighbor(nextId)}
          className="fixed right-3 top-1/2 -translate-y-1/2 z-40 w-12 h-12 rounded-full bg-white/90 backdrop-blur border border-surface-dark/40 shadow-lg hover:shadow-xl hover:bg-white text-dark flex items-center justify-center transition-all"
          aria-label="Next creative"
          title="Next creative (→)"
        >
          <i className="ri-arrow-right-s-line text-2xl" />
        </button>
      )}
      {/* ===== Top bar — close + title + delete ===== */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-surface-dark/40">
        {/* max-w matches StudioFinishEditor's content width (843 + 589 + 20
            gap = 1452px) so the Delete button at the far right lands
            ALIGNED with the editor's right edge instead of hanging out
            in the 148px margin (Jun 17 user fix). */}
        <div className="max-w-[1452px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button
            onClick={handleClose}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-dark hover:bg-surface-dark/40 transition-colors"
            aria-label="Back"
          >
            <i className="ri-arrow-left-line text-lg" />
            <span className="hidden sm:inline text-sm font-medium">My Creatives</span>
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-lg font-bold text-dark truncate">
              {post.title || 'Untitled creative'}
            </h1>
            <p className="text-xs text-dark-muted">
              {new Date(post.created_at).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
              {post.aspect_ratio ? ` · ${post.aspect_ratio}` : ''}
            </p>
          </div>

          {/* Version picker (Jun 17). Shown only when the chain has more
              than 1 version. Lets the user scrub back to an earlier
              snapshot without leaving the page; selecting an old version
              swaps the displayed image. Editing from there creates a new
              head. */}
          {versions.length > 1 && (() => {
            const headIdx = versions.findIndex((v) => v.id === post.id);
            const currentLabel = headIdx >= 0 ? `v${headIdx + 1}` : '?';
            return (
              <div className="relative flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setVersionPickerOpen((open) => !open)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface-panel border border-surface-dark/40 text-xs font-bold text-dark hover:bg-surface-dark/40 transition-colors"
                  title="Switch version"
                >
                  <i className="ri-history-line text-sm" />
                  <span>{currentLabel} of {versions.length}</span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform ${versionPickerOpen ? 'rotate-180' : ''}`} />
                </button>
                {versionPickerOpen && (
                  <>
                    {/* Backdrop catches outside clicks */}
                    <div
                      className="fixed inset-0 z-30"
                      onClick={() => setVersionPickerOpen(false)}
                    />
                    <div className="absolute right-0 top-full mt-1 z-40 min-w-[200px] bg-white rounded-xl border border-surface-dark/40 shadow-lg overflow-hidden">
                      {versions.map((v, i) => {
                        const isCurrent = v.id === post.id;
                        const isHead = v.is_head;
                        return (
                          <button
                            key={v.id}
                            type="button"
                            onClick={() => {
                              // Navigate to the version's OWN url — do NOT
                              // setPost(v) with the narrow picker record. The
                              // cold-load effect refetches whenever post.id !==
                              // the URL's postId, so setPost(v) would instantly
                              // get clobbered back to the head (the URL still
                              // pointed at it). Changing the URL makes that same
                              // effect fetch the FULL version row (base +
                              // overlays), so an old version opens fully
                              // editable, not just as a flat preview.
                              if (v.id !== post.id) {
                                navigate(`/creatives/${v.id}`, { replace: true });
                              }
                              setVersionPickerOpen(false);
                            }}
                            className={`w-full px-3 py-2 flex items-center gap-2 text-left text-xs transition-colors ${
                              isCurrent
                                ? 'bg-primary/10 text-primary font-bold'
                                : 'hover:bg-surface text-dark'
                            }`}
                          >
                            <span className="font-mono w-7">v{i + 1}</span>
                            <span className="flex-1 truncate">
                              {new Date(v.created_at).toLocaleDateString(undefined, {
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: 'numeric',
                              })}
                            </span>
                            {isHead && (
                              <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-bold uppercase">
                                head
                              </span>
                            )}
                            {isCurrent && !isHead && (
                              <i className="ri-check-line text-base" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          })()}
          <button
            onClick={handleDelete}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-red-600 hover:bg-red-50 transition-colors text-sm font-medium"
            aria-label="Delete creative"
            title="Delete this creative"
          >
            <i className="ri-delete-bin-line text-lg" />
            <span>Delete image</span>
          </button>
        </div>
      </header>

      {/* ===== Body ===== */}
      {/* max-w matches the header (Jun 17) — same 1452px so the editor's
          1452-wide grid sits centered under the aligned header. */}
      <div className="max-w-[1452px] mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {isVideo ? (
          // Video path keeps the legacy 2-column layout — StudioFinishEditor
          // handles still images (single + carousel) but not <video> playback.
          // Carousels (Jun 22) now route through the editor with the unzipped
          // frames; the frame thumb row + active hero is the same UX Product
          // Shot users see post-render via StudioFinishEditor.
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(320px,2fr)] gap-4 sm:gap-6">
          <section className="rounded-2xl bg-surface-panel border border-surface-dark/40 p-3 sm:p-4">
            {(
              <div className="flex items-center justify-center bg-black rounded-xl overflow-hidden">
                <video
                  src={imageUrl}
                  controls
                  loop
                  playsInline
                  className="max-w-full max-h-[70vh]"
                />
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={handleDownload}
                className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-white border border-surface-dark/50 text-sm font-bold text-dark hover:bg-surface-dark/40 transition-colors"
              >
                <i className="ri-download-line" />
                Download
              </button>
              {SCHEDULING_ENABLED && (() => {
                const summary = summarisePostSchedule(postScheduledTiles);
                const isScheduled = summary.kind === 'scheduled';
                return (
                  <button
                    onClick={handleSchedule}
                    title={isScheduled ? 'Edit or cancel existing schedule' : 'Schedule for later'}
                    className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-white border border-surface-dark/50 text-sm font-bold text-dark hover:bg-surface-dark/40 transition-colors"
                  >
                    <i className={isScheduled ? 'ri-calendar-event-line text-primary' : 'ri-calendar-line'} />
                    {isScheduled ? 'Edit schedule' : 'Schedule'}
                  </button>
                );
              })()}
            </div>
          </section>

          {/* ---------- Action panel ---------- */}
          <aside className="space-y-4 sm:space-y-6">
            {/* ===== Publish section — hidden for V1 (SOCIAL_PUBLISHING_ENABLED) ===== */}
            {SOCIAL_PUBLISHING_ENABLED && (
            <section className="rounded-2xl bg-white border border-surface-dark/40 p-4 sm:p-5">
              <header className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-dark uppercase tracking-wide">
                  Publish
                </h2>
                {channelsConnectedCount === 0 && (
                  <Link
                    to="/gallery"
                    className="text-xs text-primary font-medium hover:underline"
                  >
                    Connect channel →
                  </Link>
                )}
              </header>
              {isVideo ? (
                <p className="text-sm text-dark-muted">
                  Video publishing isn't wired up yet — download the MP4 and
                  post it inside TikTok / Instagram / Facebook (that's also
                  where you add trending audio).
                </p>
              ) : channelsConnectedCount === 0 ? (
                <p className="text-sm text-dark-muted">
                  Connect Facebook or Instagram from the sidebar to publish from here.
                </p>
              ) : (
                <div className="space-y-3">
                  {CHANNELS.map((meta) => {
                    const connected = channelsConnected[meta.id];
                    const selected = selectedChannels.has(meta.id);
                    const status = channelStatus[meta.id];
                    const accountLabel =
                      meta.id === 'facebook'
                        ? fbStatus?.pageName
                        : fbStatus?.instagramUsername
                          ? `@${fbStatus.instagramUsername}`
                          : undefined;
                    return (
                      <div
                        key={meta.id}
                        className={`rounded-xl border transition-colors ${
                          selected
                            ? 'border-primary/60 bg-primary-light/40'
                            : 'border-surface-dark/40 bg-surface-panel'
                        } ${!connected ? 'opacity-50' : ''}`}
                      >
                        <button
                          type="button"
                          onClick={() => connected && handleToggleChannel(meta.id)}
                          disabled={!connected || status.kind === 'posting'}
                          className="w-full flex items-center gap-3 p-3 text-left disabled:cursor-not-allowed"
                        >
                          <span
                            className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-lg shrink-0"
                            style={{ backgroundColor: meta.color }}
                          >
                            <i className={meta.icon} />
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-dark">{meta.label}</p>
                            <p className="text-xs text-dark-muted truncate">
                              {!connected
                                ? 'Not connected'
                                : accountLabel || 'Connected'}
                            </p>
                          </div>
                          {connected && (
                            <span
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                                selected
                                  ? 'bg-primary border-primary'
                                  : 'bg-white border-surface-dark/60'
                              }`}
                            >
                              {selected && <i className="ri-check-line text-white text-xs" />}
                            </span>
                          )}
                        </button>
                        {selected && connected && (
                          <div className="px-3 pb-3 space-y-2">
                            <div className="flex items-center justify-end">
                              <button
                                type="button"
                                onClick={() => handleRegenerateCaption(meta.id)}
                                disabled={captionLoading[meta.id]}
                                title="Generate a fresh caption with AI (overwrites the current text)"
                                className="
                                  inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                                  text-[10px] font-bold uppercase tracking-wider
                                  bg-primary-light text-primary border border-primary/20
                                  hover:bg-primary/10 transition-colors cursor-pointer
                                  disabled:opacity-40 disabled:cursor-not-allowed
                                "
                              >
                                {captionLoading[meta.id] ? (
                                  <><i className="ri-loader-4-line animate-spin" />Regenerating…</>
                                ) : (
                                  <><i className="ri-magic-line" />Regenerate with AI</>
                                )}
                              </button>
                            </div>
                            {captionLoading[meta.id] ? (
                              <div className="h-20 rounded-lg bg-white border border-surface-dark/40 flex items-center justify-center">
                                <span className="text-xs text-dark-muted">
                                  Writing caption…
                                </span>
                              </div>
                            ) : (
                              <textarea
                                value={captions[meta.id]}
                                onChange={(e) =>
                                  setCaptions((p) => ({ ...p, [meta.id]: e.target.value }))
                                }
                                placeholder={`Caption for ${meta.label}…`}
                                rows={3}
                                className="w-full text-sm rounded-lg border border-surface-dark/50 px-3 py-2 bg-white text-dark resize-none focus:outline-none focus:border-primary"
                              />
                            )}
                            {status.kind === 'posted' && (
                              <p className="text-xs text-green-600 font-medium">
                                ✓ Posted to {meta.label}
                              </p>
                            )}
                            {status.kind === 'error' && (
                              <p className="text-xs text-red-600 font-medium">
                                {status.message}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <button
                    onClick={handlePublish}
                    disabled={
                      selectedChannels.size === 0 ||
                      Array.from(selectedChannels).some(
                        (c) => channelStatus[c].kind === 'posting',
                      )
                    }
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary text-white text-sm font-bold hover:bg-primary-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {Array.from(selectedChannels).some(
                      (c) => channelStatus[c].kind === 'posting',
                    ) ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        Publishing…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill" />
                        Post to {selectedChannels.size || 0} channel
                        {selectedChannels.size === 1 ? '' : 's'}
                      </>
                    )}
                  </button>
                </div>
              )}
            </section>
            )}

            {/* ===== Reimagine section ===== */}
            {/* Hidden for video posts — reimagine transforms a source
                IMAGE; there's no frame to feed it from an MP4. */}
            {!isVideo && (
            <section className="rounded-2xl bg-white border border-surface-dark/40 p-4 sm:p-5">
              <header className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-dark uppercase tracking-wide">
                  Reimagine with AI
                </h2>
                <span className="text-xs text-dark-muted">{REIMAGINE_COST} credit</span>
              </header>
              <p className="text-xs text-dark-muted mb-3">
                Describe a change. A new tile will appear in My Creatives — your
                original stays put.
              </p>
              <textarea
                value={reimaginePromptText}
                onChange={(e) => setReimaginePromptText(e.target.value)}
                placeholder="e.g. Make the background a luxurious marble counter with soft window light…"
                rows={3}
                disabled={reimagining}
                className="w-full text-sm rounded-lg border border-surface-dark/50 px-3 py-2 bg-white text-dark resize-none focus:outline-none focus:border-primary disabled:opacity-60"
              />
              {reimagineError && (
                <p className="mt-2 text-xs text-red-600 font-medium">{reimagineError}</p>
              )}
              <button
                onClick={handleReimagine}
                disabled={!reimaginePromptText.trim() || reimagining}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-accent text-white text-sm font-bold hover:bg-accent/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {reimagining ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Reimagining… (~25s)
                  </>
                ) : (
                  <>
                    <i className="ri-magic-line" />
                    Generate new variation
                  </>
                )}
              </button>
            </section>
            )}
          </aside>
          </div>
        ) : editorFrames.length === 0 ? (
          // Initial frame state hasn't synced yet (useEffect runs after
          // first render). Show a blank placeholder rather than mount
          // the editor with frames=[] which crashes on activeImage
          // dereferences down the JSX tree.
          <div className="rounded-2xl bg-surface-panel border border-surface-dark/40 p-8 text-center text-sm text-dark-muted">
            Loading editor…
          </div>
        ) : isTextOverlay ? (
          // Text-overlay tile — edit via the SAME JsonSocialPost renderer that
          // baked the thumbnail (pixel-perfect parity), NOT the canvas editor.
          <div className="space-y-4 sm:space-y-6">
            <OverlayEditor
              post={post}
              onSaved={(updated) => {
                setPost(updated);
                setToast({ message: 'Saved', type: 'success' });
              }}
            />
          </div>
        ) : (
          // Single-image path — StudioFinishEditor as the full-width
          // editor surface. Publish/captions still live in the existing
          // 2-column path for now (carousel + video); single-image users
          // get the editor + a Publish row below.
          <div className="space-y-4 sm:space-y-6">
            {/* Jul 12/14 — "Edit Text" lives in the StudioFinishEditor action row
                (footer) alongside Re-render/Post/Download/Save. Lifts baked-in
                text into editable overlays. Works for singles (2 cr) AND carousels
                (8 cr, per-frame). Hidden once the post is already editable. */}
            <StudioFinishEditor
              frames={editorFrames}
              aspectRatio={(['1:1','4:5','2:3','9:16','16:9','1.91:1'].includes(post.aspect_ratio ?? '') ? post.aspect_ratio : '1:1') as '1:1' | '4:5' | '2:3' | '9:16' | '16:9' | '1.91:1'}
              activeFrameIdx={editorActiveFrameIdx}
              onActiveFrameChange={setEditorActiveFrameIdx}
              overlays={editorOverlays}
              onOverlaysChange={(o) => { setEditorOverlays(o); setEditorDirty(true); }}
              logoOverlays={editorLogoOverlays}
              onLogoOverlaysChange={(l) => { setEditorLogoOverlays(l); setEditorDirty(true); }}
              brandLogoUrl={editorBrandLogoUrl}
              brandColors={editorBrandColors}
              suggestedHeadline={post.headline ?? null}
              suggestedTagline={post.tagline ?? null}
              editPrompt={editorPrompt}
              onEditPromptChange={setEditorPrompt}
              promptEditing={editorPromptEditing}
              editError={editorEditError}
              // Jun 22 — carousels go through the same Apply Edit + Save
              // Edits pipeline as singles. Apply Edit targets the ACTIVE
              // frame (multi-frame branch in handleEditorPromptEdit).
              // Save Edits multi-frame flattens + re-zips both flat +
              // base via handleEditorSave's carousel branch.
              onPromptEdit={handleEditorPromptEdit}
              isFree={(planTier ?? 'free') === 'free'}
              frameCount={isCarousel ? editorFrames.length : 1}
              retryOpen={editorRetryOpen}
              onToggleRetry={handleEditorToggleRetry}
              onCloseRetry={handleEditorCloseRetry}
              onRegenerateWithQuality={handleEditorRegenerateWithQuality}
              saveState={editorSaveState}
              onRetrySave={handleEditorRetrySave}
              onSchedule={handlePost}
              onDownload={handleEditorDownload}
              downloading={editorDownloading}
              onManualSave={handleEditorSave}
              creativeDirty={editorDirty}
              retryRendering={editorRetryRendering}
              // Marquee edit (Jun 24)
              marqueeApplying={marqueeApplying}
              marqueeUndoing={marqueeUndoing}
              marqueeHistoryCount={post.base_image_path_history?.length ?? 0}
              onMarqueeApply={handleMarqueeApply}
              onMarqueeUndo={handleMarqueeUndo}
              // Marquee preview → Keep/Discard (Jul 17). When active, the editor
              // shows the rendered-but-unsaved edit + a confirm bar and locks
              // the other controls until the user decides.
              marqueePreviewActive={marqueePreview !== null}
              marqueeKeepSaving={editorSaveState === 'saving'}
              onMarqueeKeep={handleMarqueeKeep}
              onMarqueeDiscard={handleMarqueeDiscard}
              // Edit Text (Jul 12/14) — footer pill, 2 cr. Carousels are PER-SLIDE:
              // always available (acts on the slide in view; the editor labels it
              // "Edit Text · Slide N"). Singles hide once already editable.
              onEditText={
                isCarousel
                  ? handleEditText
                  : (post.base_image_url && (post.overlay_json?.length ?? 0) > 0)
                    ? undefined
                    : handleEditText
              }
              editingText={preparingText}
              editTextCredits={2}
              editTextDisabled={balance < 2}
              editTextTitle={balance < 2 ? 'Need 2 credits' : undefined}
            />
          </div>
        )}
      </div>

      {/* Schedule modal — opens from the Schedule button above the image.
          editingTiles drives mode:
          - empty array → create a fresh schedule for this post.
          - non-empty   → existing schedule cohort; user can toggle
                          platforms, change time, or cancel. */}
      {SCHEDULING_ENABLED && scheduling !== null && post && (
        <ScheduleModal
          editingTiles={scheduling.editingTiles.length > 0 ? scheduling.editingTiles : null}
          createDay={new Date()}
          posts={[post]}
          fbStatus={fbStatus}
          onClose={() => setScheduling(null)}
          onSaved={() => {
            const wasEditing = scheduling.editingTiles.length > 0;
            setScheduling(null);
            void refreshScheduledTiles();
            setToast({
              message: wasEditing ? 'Schedule updated' : 'Scheduled',
              type: 'success',
            });
          }}
        />
      )}

      {/* Publish-now modal (Jun 17). Action-row Post button opens this
          with BOTH channels pre-enabled — user toggles off whichever
          channel they want to skip. Per-channel captions persist so
          IG keeps its hashtags + FB keeps its longer copy independently. */}
      {SOCIAL_PUBLISHING_ENABLED && publishOpen && post && (
        <PostToSocialModal
          post={post}
          initialEnabled={['facebook', 'instagram']}
          headerLabel="Post or schedule"
          onClose={() => setPublishOpen(false)}
          onSuccess={(channelsPublished, submittedMode) => {
            const names = channelsPublished
              .map((c) =>
                c === 'instagram' ? 'Instagram'
                  : c === 'facebook' ? 'Facebook'
                  : c === 'tiktok' ? 'TikTok'
                  : 'X',
              )
              .join(' + ');
            const verb = submittedMode === 'now' ? 'Posted to' : 'Scheduled for';
            setToast({
              message: `${verb} ${names}`,
              type: 'success',
            });
            setPublishOpen(false);
          }}
          onCaptionSaved={(channel, caption) => {
            // Mirror the cached captions onto the loaded post so the next
            // open reads the freshly-saved draft instead of re-firing AI.
            setPost((prev) =>
              prev
                ? { ...prev, captions: { ...prev.captions, [channel]: caption } }
                : prev,
            );
          }}
        />
      )}

      {/* Floating toast — bottom-center. Mirrors the gallery treatment so
          success/error colors stay consistent across surfaces. */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] pointer-events-none">
          <div
            className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium shadow-lg ${
              toast.type === 'success'
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}
          >
            <i className={toast.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} />
            {toast.message}
          </div>
        </div>
      )}

      {/* Synth breadcrumb panel (Jun 21) — recipe-synth provenance. Copy a
          donor recipe id (esp. the LAYOUT donor) to find it in the moodboard
          builder. Shown on any local run (dev server OR a locally-served prod
          build — import.meta.env.DEV is false on the latter) and on the prod
          domain only with ?debug (hidden from normal customers, visible for
          our own inspection). */}
      {(import.meta.env.DEV ||
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        new URLSearchParams(window.location.search).has('debug')) &&
        post?.synth && <SynthDevPanel synth={post.synth} />}
    </div>
  );
}

// ── Dev-only synth breadcrumb panel ─────────────────────────────────────────
// Surfaces render_config.synth (recipe-synth recombination provenance) so the
// curator can verify which moodboards donated each visual group. Clicking a
// donor row inline-previews that moodboard's thumbnail + key fields directly
// inside the panel — no /moodboard-builder navigation, no waiting on the full
// library to render.
//
// Visible only on synth renders (top-up / Generate-20-more) in dev mode.

// Single-cell sidecar shape we need for the preview. The list endpoint
// returns every field on every moodboard — we keep only what's shown here.
interface MoodboardPreview {
  id: string;
  vibe: string;
  vibe_slug: string;
  preview_url?: string | null;
  image_url?: string | null;
  style_summary?: string;
  product_types?: string[];
  ready_for_production?: boolean;
  requires_model?: boolean;
  model_relation?: 'wearing' | 'holding' | 'using';
}

// Module-scoped — survives panel collapse/expand AND across multiple
// /creatives/:id navigations during the same dev session. One fetch per
// page lifetime instead of one per donor click.
let _moodboardLibraryCache: Map<string, MoodboardPreview> | null = null;
let _moodboardLibraryInflight: Promise<Map<string, MoodboardPreview>> | null = null;

async function _loadMoodboardLibrary(): Promise<Map<string, MoodboardPreview>> {
  if (_moodboardLibraryCache) return _moodboardLibraryCache;
  if (_moodboardLibraryInflight) return _moodboardLibraryInflight;
  _moodboardLibraryInflight = (async () => {
    const { API_BASE, getAuthHeaders } = await import('@/lib/api');
    const headers = await getAuthHeaders();
    const resp = await fetch(`${API_BASE}/api/dev/moodboard/list`, { headers });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = (await resp.json()) as {
      groups: Record<string, MoodboardPreview[]>;
    };
    const map = new Map<string, MoodboardPreview>();
    for (const list of Object.values(data.groups || {})) {
      for (const rec of list) {
        if (rec.vibe_slug && rec.id) map.set(`${rec.vibe_slug}/${rec.id}`, rec);
      }
    }
    _moodboardLibraryCache = map;
    _moodboardLibraryInflight = null;
    return map;
  })();
  return _moodboardLibraryInflight;
}

function SynthDevPanel({ synth }: { synth: Record<string, unknown> }) {
  const [open, setOpen] = useState(true);
  const [library, setLibrary] = useState<Map<string, MoodboardPreview> | null>(
    _moodboardLibraryCache,
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [promptOpen, setPromptOpen] = useState(false);

  // Eager-load the moodboard library so both thumbnails show without a click.
  useEffect(() => {
    if (library) return;
    let cancelled = false;
    void _loadMoodboardLibrary()
      .then((lib) => { if (!cancelled) setLibrary(lib); })
      .catch((err) => { if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err)); });
    return () => { cancelled = true; };
  }, [library]);

  const str = (v: unknown): string => (typeof v === 'string' ? v : '');
  const baseId = str(synth.recipe_id);
  const isFusion = synth.rt_fusion === true;
  const partnerId = str(synth.rt_fusion_partner);
  const prompt = str(synth.prompt);

  // One recipe card: eagerly-loaded thumbnail + id + vibe/type tags.
  const RecipeCard = ({ id, tag }: { id: string; tag: string }) => {
    const rec = library?.get(id);
    const src = rec?.preview_url || rec?.image_url || '';
    return (
      <div className="flex-1 min-w-0">
        <div className="mb-1 flex items-center gap-1.5">
          <span className="px-1.5 py-0.5 rounded bg-fuchsia-500/20 text-fuchsia-200 text-[9px] font-bold uppercase tracking-wide">
            {tag}
          </span>
          {rec?.vibe && (
            <span className="px-1.5 py-0.5 rounded bg-white/10 text-[9px] font-semibold uppercase">{rec.vibe}</span>
          )}
        </div>
        {src ? (
          <img
            src={src}
            alt={rec?.style_summary || id}
            className="w-full aspect-square object-cover rounded-md bg-white/5"
            loading="lazy"
          />
        ) : (
          <div className="w-full aspect-square rounded-md bg-white/5 flex items-center justify-center text-[10px] text-white/40">
            {library ? 'not in library' : 'loading…'}
          </div>
        )}
        <button
          type="button"
          onClick={() => void navigator.clipboard?.writeText(id)}
          title="Copy recipe id"
          className="mt-1 w-full truncate text-left text-[10px] font-mono text-sky-300 hover:text-white cursor-pointer"
        >
          {id || '—'}
        </button>
        {(rec?.product_types || []).length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {(rec?.product_types || []).map((t) => (
              <span key={t} className="px-1 py-0.5 rounded bg-white/10 text-[9px] uppercase">{t}</span>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed bottom-6 left-6 z-[70] w-80 max-h-[80vh] overflow-y-auto rounded-xl bg-zinc-900/95 text-white shadow-2xl ring-1 ring-white/10 backdrop-blur">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-3 py-2 text-left cursor-pointer"
      >
        <span className="text-xs font-bold flex items-center gap-1.5">
          <i className="ri-blender-line text-fuchsia-300" />
          {isFusion ? 'Fused recipes' : 'Recipe'}
          <span className="text-[9px] font-normal text-white/40">dev</span>
        </span>
        <i className={`text-white/50 ${open ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {loadError && (
            <div className="text-[11px] text-red-300">Couldn’t load moodboards: {loadError}</div>
          )}
          <div className="flex gap-2">
            <RecipeCard id={baseId} tag="A" />
            {isFusion && partnerId && <RecipeCard id={partnerId} tag="B" />}
          </div>
          {!isFusion && (
            <p className="text-[10px] text-white/40 italic">Not fused — single recipe rendered verbatim.</p>
          )}
          {prompt && (
            <details open={promptOpen} onToggle={(e) => setPromptOpen((e.target as HTMLDetailsElement).open)}>
              <summary className="cursor-pointer text-[10px] uppercase tracking-wide text-white/40 flex items-center gap-1.5">
                <i className="ri-file-text-line" /> Prompt
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); void navigator.clipboard?.writeText(prompt); }}
                  title="Copy prompt"
                  className="ml-auto text-white/40 hover:text-white cursor-pointer"
                >
                  <i className="ri-file-copy-line" />
                </button>
              </summary>
              <pre className="mt-1.5 max-h-64 overflow-y-auto whitespace-pre-wrap text-[10px] leading-snug text-white/70 bg-black/30 rounded-md p-2">
                {prompt}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
